import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const NeighborhoodIntelligence = () => {
  const navigate = useNavigate();

  const popularNeighborhoods = [
    {
      id: 1,
      name: "Koramangala",
      city: "Bangalore",
      image: "https://images.unsplash.com/photo-1582407947304-fd86f028f716?w=800&h=600&fit=crop",
      averageRent: "₹35,000",
      averagePrice: "₹85 Lakh",
      amenityScore: 9.2,
      lifestyle: "Tech Hub",
      highlights: ["IT Companies", "Restaurants", "Nightlife", "Shopping"],
      transportScore: 8.5,
      safetyScore: 8.8,
      priceGrowth: "+12%",
      totalProperties: 1250
    },
    {
      id: 2,
      name: "Bandra West",
      city: "Mumbai",
      image: "https://images.unsplash.com/photo-1567157577867-05ccb1388e66?w=800&h=600&fit=crop",
      averageRent: "₹55,000",
      averagePrice: "₹2.5 Cr",
      amenityScore: 9.5,
      lifestyle: "Premium Living",
      highlights: ["Sea View", "Cafes", "Bollywood", "Luxury"],
      transportScore: 9.0,
      safetyScore: 9.2,
      priceGrowth: "+8%",
      totalProperties: 890
    },
    {
      id: 3,
      name: "Connaught Place",
      city: "Delhi",
      image: "https://images.unsplash.com/photo-1587474260584-136574528ed5?w=800&h=600&fit=crop",
      averageRent: "₹45,000",
      averagePrice: "₹1.8 Cr",
      amenityScore: 8.9,
      lifestyle: "Business District",
      highlights: ["Metro", "Shopping", "Heritage", "Corporate"],
      transportScore: 9.5,
      safetyScore: 8.5,
      priceGrowth: "+15%",
      totalProperties: 750
    },
    {
      id: 4,
      name: "Anna Nagar",
      city: "Chennai",
      image: "https://images.unsplash.com/photo-1582407947304-fd86f028f716?w=800&h=600&fit=crop",
      averageRent: "₹25,000",
      averagePrice: "₹65 Lakh",
      amenityScore: 8.3,
      lifestyle: "Family Friendly",
      highlights: ["Schools", "Parks", "Hospitals", "Peaceful"],
      transportScore: 7.8,
      safetyScore: 9.0,
      priceGrowth: "+10%",
      totalProperties: 980
    },
    {
      id: 5,
      name: "Hitech City",
      city: "Hyderabad",
      image: "https://images.unsplash.com/photo-1570129477492-45c003edd2be?w=800&h=600&fit=crop",
      averageRent: "₹30,000",
      averagePrice: "₹75 Lakh",
      amenityScore: 8.7,
      lifestyle: "IT Corridor",
      highlights: ["Tech Parks", "Modern", "Infrastructure", "Growth"],
      transportScore: 8.2,
      safetyScore: 8.6,
      priceGrowth: "+18%",
      totalProperties: 1100
    },
    {
      id: 6,
      name: "Salt Lake",
      city: "Kolkata",
      image: "https://images.unsplash.com/photo-1484154218962-a197022b5858?w=800&h=600&fit=crop",
      averageRent: "₹20,000",
      averagePrice: "₹45 Lakh",
      amenityScore: 7.9,
      lifestyle: "Cultural Hub",
      highlights: ["IT Sector", "Lakes", "Culture", "Affordable"],
      transportScore: 7.5,
      safetyScore: 8.2,
      priceGrowth: "+7%",
      totalProperties: 650
    }
  ];

  const handleNeighborhoodClick = (neighborhood) => {
    navigate('/neighborhood-intelligence-hub', { state: { neighborhood } });
  };

  const getScoreColor = (score) => {
    if (score >= 9) return 'text-success';
    if (score >= 8) return 'text-secondary';
    if (score >= 7) return 'text-warning';
    return 'text-error';
  };

  const getGrowthColor = (growth) => {
    const value = parseInt(growth.replace('%', '').replace('+', ''));
    if (value >= 15) return 'text-success';
    if (value >= 10) return 'text-secondary';
    if (value >= 5) return 'text-warning';
    return 'text-error';
  };

  return (
    <section className="py-16 bg-surface">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-text-primary mb-4">
            Neighborhood Intelligence
          </h2>
          <p className="text-text-secondary max-w-2xl mx-auto">
            Discover the perfect neighborhood with detailed insights on lifestyle, amenities, and market trends
          </p>
        </div>

        {/* Neighborhoods Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {popularNeighborhoods.map((neighborhood) => (
            <div
              key={neighborhood.id}
              className="bg-canvas rounded-xl shadow-property-card border border-border overflow-hidden hover:shadow-modal transition-smooth cursor-pointer group"
              onClick={() => handleNeighborhoodClick(neighborhood)}
            >
              {/* Neighborhood Image */}
              <div className="relative h-48 overflow-hidden">
                <Image
                  src={neighborhood.image}
                  alt={neighborhood.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-smooth"
                />
                
                {/* Overlay Info */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                
                <div className="absolute bottom-4 left-4 text-white">
                  <h3 className="text-xl font-semibold mb-1">{neighborhood.name}</h3>
                  <p className="text-sm opacity-90">{neighborhood.city}</p>
                </div>

                {/* Lifestyle Badge */}
                <div className="absolute top-4 right-4">
                  <span className="bg-primary/90 backdrop-blur-sm text-primary-foreground px-3 py-1 rounded-full text-sm font-medium">
                    {neighborhood.lifestyle}
                  </span>
                </div>
              </div>

              {/* Neighborhood Details */}
              <div className="p-6">
                {/* Price Information */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <div className="text-sm text-text-secondary mb-1">Avg. Rent</div>
                    <div className="font-semibold text-primary">{neighborhood.averageRent}</div>
                  </div>
                  <div>
                    <div className="text-sm text-text-secondary mb-1">Avg. Price</div>
                    <div className="font-semibold text-secondary">{neighborhood.averagePrice}</div>
                  </div>
                </div>

                {/* Scores */}
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="text-center">
                    <div className={`text-lg font-bold ${getScoreColor(neighborhood.amenityScore)}`}>
                      {neighborhood.amenityScore}
                    </div>
                    <div className="text-xs text-text-secondary">Amenities</div>
                  </div>
                  <div className="text-center">
                    <div className={`text-lg font-bold ${getScoreColor(neighborhood.transportScore)}`}>
                      {neighborhood.transportScore}
                    </div>
                    <div className="text-xs text-text-secondary">Transport</div>
                  </div>
                  <div className="text-center">
                    <div className={`text-lg font-bold ${getScoreColor(neighborhood.safetyScore)}`}>
                      {neighborhood.safetyScore}
                    </div>
                    <div className="text-xs text-text-secondary">Safety</div>
                  </div>
                </div>

                {/* Highlights */}
                <div className="mb-4">
                  <div className="flex flex-wrap gap-2">
                    {neighborhood.highlights.map((highlight, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 bg-muted text-text-secondary text-xs rounded-md"
                      >
                        {highlight}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Bottom Stats */}
                <div className="flex items-center justify-between pt-4 border-t border-border">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center text-sm text-text-secondary">
                      <Icon name="TrendingUp" size={14} className="mr-1" />
                      <span className={`font-medium ${getGrowthColor(neighborhood.priceGrowth)}`}>
                        {neighborhood.priceGrowth}
                      </span>
                    </div>
                    <div className="flex items-center text-sm text-text-secondary">
                      <Icon name="Building" size={14} className="mr-1" />
                      {neighborhood.totalProperties}
                    </div>
                  </div>
                  
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="ArrowRight"
                    iconPosition="right"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleNeighborhoodClick(neighborhood);
                    }}
                  >
                    Explore
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Explore More Button */}
        <div className="text-center">
          <Button
            variant="outline"
            size="lg"
            iconName="MapPin"
            iconPosition="left"
            onClick={() => navigate('/neighborhood-intelligence-hub')}
          >
            Explore All Neighborhoods
          </Button>
        </div>
      </div>
    </section>
  );
};

export default NeighborhoodIntelligence;